import { Trans } from '@lingui/react/macro';
import { Typography } from '@remarkable/ark-web';

import type { ChangeEmailInfo } from 'src/api/endpoints/storeApi.types';
import { VerifyEmailChangeError } from 'src/apps/verifyNewEmail/app/components/VerifyEmailChangeError';
import { isExpired } from 'src/apps/verifyNewEmail/app/utils';
import { Button, NotificationBox } from 'src/components';

interface EmailChangeStatusContentProps {
  emailChangeInfo: ChangeEmailInfo;
  cancelEmailChange: () => void;
  confirmEmailChange: () => void;
  hasError: boolean;
  confirmEmailChangeIsLoading: boolean;
}

export const VerifyEmailChangeUnknownError = () => (
  <VerifyEmailChangeError
    title={<Trans>We couldn&apos;t verify your email address</Trans>}
  >
    <Typography variant="body-md">
      <Trans>
        Go to <b>Account settings</b> to try again.
      </Trans>
    </Typography>
  </VerifyEmailChangeError>
);

export const VerifyEmailChangeContent = (
  props: EmailChangeStatusContentProps
) => {
  const {
    emailChangeInfo,
    cancelEmailChange,
    confirmEmailChange,
    hasError,
    confirmEmailChangeIsLoading,
  } = props;

  if (hasError) {
    return <VerifyEmailChangeUnknownError />;
  }

  switch (emailChangeInfo.status) {
    case 'pending': {
      if (isExpired(emailChangeInfo)) {
        return (
          <VerifyEmailChangeError
            title={<Trans>Your verification link has expired</Trans>}
          >
            <Typography variant="body-md">
              <Trans>
                Go to <b>Account settings</b> to try again.
              </Trans>
            </Typography>
          </VerifyEmailChangeError>
        );
      }

      const oldEmail = emailChangeInfo.oldEmail;
      const newEmail = emailChangeInfo.newEmail;

      return (
        <div
          className="-mt-32 flex flex-col gap-24 text-center"
          data-cy="pending-email-change"
        >
          <Typography variant="body-md">
            <Trans>
              The email linked to this account will change from{' '}
              <strong>{oldEmail}</strong> to <strong>{newEmail}</strong>.
            </Trans>
          </Typography>
          <Typography variant="body-md">
            <Trans>
              If everything looks correct, select &quot;Change email&quot; below
            </Trans>
          </Typography>
          <NotificationBox variant="warning" title={<Trans>Note</Trans>}>
            <Trans>
              It may take up to 8 hours for your new email address to appear on
              your paper tablet and in the apps.
            </Trans>
          </NotificationBox>
          <div className="flex w-full flex-col justify-center gap-16 lm:flex-row">
            <Button
              variant="secondary"
              onClick={cancelEmailChange}
              className="w-full lm:w-fit"
            >
              <span>
                <Trans>Cancel</Trans>
              </span>
            </Button>
            <Button
              onClick={confirmEmailChange}
              className="w-full lm:w-fit"
              variant="primary"
              loading={confirmEmailChangeIsLoading}
              disabled={confirmEmailChangeIsLoading}
            >
              <span>
                <Trans>Change email</Trans>
              </span>
            </Button>
          </div>
        </div>
      );
    }
    case 'expired':
      return (
        <VerifyEmailChangeError
          title={<Trans>Your verification link has expired</Trans>}
        >
          <Typography variant="body-md">
            <Trans>
              Go to <b>Account settings</b> to try again.
            </Trans>
          </Typography>
        </VerifyEmailChangeError>
      );
    case 'cancelled':
      return (
        <VerifyEmailChangeError
          title={<Trans>We won&apos;t change your email address</Trans>}
        >
          <Typography variant="body-md">
            <Trans>
              We&apos;ve canceled your request to change your email address, and
              the verification link is no longer active.
            </Trans>
          </Typography>
        </VerifyEmailChangeError>
      );
  }

  return <VerifyEmailChangeUnknownError />;
};
